import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { mergeSavedMultipliers } from '~/components/Flights/Charter/LoyaltyPoints/loyaltyDraft';
import {
  CharterLoyaltyPointMultiplierFilters,
  CharterLoyaltyPointMultiplierResult,
  CharterLoyaltyPointMultiplierUpdateRequestBody,
} from '~/components/Flights/Charter/charter.types';

import { fetchCharterLoyaltyPointMultipliers, updateCharterLoyaltyPointMultipliers } from '~/services/CharterService';

import { charterLoyaltyPointMultiplierQueryKeys } from './charterLoyaltyPointMultiplierQueryKeys';

export function useGetCharterLoyaltyPointMultipliers(filters: CharterLoyaltyPointMultiplierFilters | null) {
  return useQuery({
    queryKey: filters
      ? charterLoyaltyPointMultiplierQueryKeys.list(filters)
      : [...charterLoyaltyPointMultiplierQueryKeys.lists(), 'disabled'],
    queryFn: async () => {
      if (!filters) {
        return [];
      }
      return await fetchCharterLoyaltyPointMultipliers(filters);
    },
    enabled: Boolean(filters),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useUpdateCharterLoyaltyPointMultipliers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CharterLoyaltyPointMultiplierUpdateRequestBody) => {
      return await updateCharterLoyaltyPointMultipliers(payload);
    },
    onSuccess: async (savedItems) => {
      // A failed refresh must not leave pre-save values available for the next edit or search.
      queryClient.setQueriesData<Array<CharterLoyaltyPointMultiplierResult>>(
        { queryKey: charterLoyaltyPointMultiplierQueryKeys.lists() },
        (results) => (results ? mergeSavedMultipliers(results, savedItems) : results),
      );
      // The page refreshes separately so a failed read cannot be reported as a failed save.
      await queryClient.invalidateQueries({
        queryKey: charterLoyaltyPointMultiplierQueryKeys.all,
        refetchType: 'none',
      });
    },
  });
}
